import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Send } from 'lucide-react';
import Button from '../components/UI/Button';
import Input from '../components/UI/Input';
import TextArea from '../components/UI/TextArea';
import Select from '../components/UI/Select';
import Card from '../components/UI/Card';

const Contact: React.FC = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    reason: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          to: 'contact@invoice-ai.fr',
          subject: `[Contact] ${formData.subject}`,
          textContent: `
            Nom: ${formData.name}
            Email: ${formData.email}
            Raison: ${formData.reason}
            Message: ${formData.message}
          `,
          htmlContent: `
            <h2>Nouveau message de contact</h2>
            <p><strong>Nom:</strong> ${formData.name}</p>
            <p><strong>Email:</strong> ${formData.email}</p>
            <p><strong>Raison:</strong> ${formData.reason}</p>
            <p><strong>Message:</strong></p>
            <p>${formData.message.replace(/\n/g, '<br>')}</p>
          `
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        reason: ''
      });
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex items-center">
          <Button 
            variant="ghost" 
            className="mr-4"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Contactez-nous</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Notre équipe est là pour vous aider
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card>
              <Card.Content>
                {success ? (
                  <div className="text-center py-8">
                    <div className="h-16 w-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Send className="h-8 w-8 text-green-500" />
                    </div>
                    <h2 className="text-2xl font-semibold mb-4">Message envoyé !</h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      Nous vous répondrons dans les plus brefs délais.
                    </p>
                    <Button
                      onClick={() => setSuccess(false)}
                    >
                      Envoyer un autre message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Input
                        label="Nom"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                      <Input
                        label="Email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <Select
                      label="Raison du contact"
                      name="reason"
                      value={formData.reason}
                      onChange={handleChange}
                      required
                      options={[
                        { value: '', label: 'Sélectionnez une raison' },
                        { value: 'support', label: 'Support technique' },
                        { value: 'billing', label: 'Question de facturation' },
                        { value: 'feature', label: 'Suggestion de fonctionnalité' },
                        { value: 'bug', label: 'Signalement de bug' },
                        { value: 'other', label: 'Autre' }
                      ]}
                    />

                    <Input
                      label="Sujet"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                    />

                    <TextArea
                      label="Message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                      required
                    />

                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        isLoading={isSubmitting}
                        leftIcon={<Send className="h-4 w-4" />}
                      >
                        Envoyer le message
                      </Button>
                    </div>
                  </form>
                )}
              </Card.Content>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <Card.Content>
                <div className="flex items-start">
                  <div className="h-10 w-10 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-primary-500" />
                  </div>
                  <div className="ml-4">
                    <h3 className="font-medium mb-1">Email</h3>
                    <a 
                      href="mailto:contact@invoice-ai.fr"
                      className="text-gray-600 dark:text-gray-400 hover:text-primary-500"
                    >
                      contact@invoice-ai.fr
                    </a>
                  </div>
                </div>
              </Card.Content>
            </Card>

            <Card>
              <Card.Content>
                <div className="flex items-start">
                  <div className="h-10 w-10 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-primary-500" />
                  </div>
                  <div className="ml-4">
                    <h3 className="font-medium mb-1">Adresse</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      651 N Broad St<br />
                      New Castle, DE 19709<br />
                      États-Unis
                    </p>
                  </div>
                </div>
              </Card.Content>
            </Card>

            <Card>
              <Card.Content>
                <div className="flex items-start">
                  <div className="h-10 w-10 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center">
                    <Phone className="h-5 w-5 text-primary-500" />
                  </div>
                  <div className="ml-4">
                    <h3 className="font-medium mb-1">Support</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Lun-Ven, 9h-18h<br />
                      Réponse sous 24h
                    </p>
                  </div>
                </div>
              </Card.Content>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;